import { useState, useEffect } from "react";
import { X, Download, Share, Plus, MoreVertical } from "lucide-react";
import { Button } from "./ui/button";

const STORAGE_KEY = "wornwell_install_prompt";

export function InstallPrompt() {
  const [showPrompt, setShowPrompt] = useState(false);
  const [deviceType, setDeviceType] = useState(null); // 'ios', 'android', or null
  const [deferredPrompt, setDeferredPrompt] = useState(null);

  useEffect(() => {
    // Check if already installed as PWA
    const isStandalone = window.matchMedia("(display-mode: standalone)").matches 
      || window.navigator.standalone === true;
    
    if (isStandalone) {
      return; // Already installed, don't show
    }

    // Check localStorage for previous dismissal
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const { dismissed, dismissedAt } = JSON.parse(stored);
      if (dismissed) {
        // If dismissed more than 7 days ago, show again
        const daysSinceDismissed = (Date.now() - dismissedAt) / (1000 * 60 * 60 * 24);
        if (daysSinceDismissed < 7) {
          return; // Recently dismissed, don't show
        }
      }
    }

    // Detect device type
    const userAgent = navigator.userAgent.toLowerCase();
    const isIOS = /iphone|ipad|ipod/.test(userAgent);
    const isAndroid = /android/.test(userAgent);
    const isMobile = isIOS || isAndroid;

    if (!isMobile) {
      return; // Desktop - don't show mobile install prompt
    }

    setDeviceType(isIOS ? "ios" : "android");

    // For Android, listen for the beforeinstallprompt event
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstall);

    // Small delay before showing to not be too aggressive
    const timer = setTimeout(() => {
      setShowPrompt(true);
    }, 2000);

    // Listen for successful installation
    window.addEventListener("appinstalled", handleAppInstalled);

    return () => {
      clearTimeout(timer);
      window.removeEventListener("beforeinstallprompt", handleBeforeInstall);
      window.removeEventListener("appinstalled", handleAppInstalled);
    };
  }, []);

  const handleAppInstalled = () => {
    setShowPrompt(false);
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ 
      installed: true, 
      installedAt: Date.now() 
    }));
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ 
      dismissed: true, 
      dismissedAt: Date.now() 
    }));
  };

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      // Android - trigger native install prompt
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === "accepted") {
        handleAppInstalled();
      }
      setDeferredPrompt(null);
    }
  };

  if (!showPrompt) return null;

  return (
    <div 
      className="fixed bottom-0 left-0 right-0 z-50 animate-in slide-in-from-bottom duration-300"
      data-testid="install-prompt-banner"
    >
      <div className="bg-primary text-primary-foreground mx-4 mb-4 rounded-lg shadow-xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-primary-foreground/10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center">
              <span className="text-primary font-heading font-bold text-sm">W</span>
            </div>
            <div>
              <p className="font-medium text-sm">Install WornWell</p>
              <p className="text-xs opacity-80">Add to your home screen</p>
            </div>
          </div>
          <button 
            onClick={handleDismiss}
            className="p-2 hover:bg-primary-foreground/10 rounded-full transition-colors"
            aria-label="Dismiss"
            data-testid="install-prompt-dismiss"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Instructions */}
        <div className="px-4 py-4">
          {deviceType === "ios" ? (
            <div className="space-y-3">
              <p className="text-sm opacity-90">To install on iPhone:</p>
              <div className="flex items-start gap-3">
                <div className="flex items-center justify-center w-6 h-6 rounded-full bg-primary-foreground/20 text-xs font-medium shrink-0">
                  1
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <span>Tap</span>
                  <Share className="w-5 h-5" />
                  <span>Share</span>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="flex items-center justify-center w-6 h-6 rounded-full bg-primary-foreground/20 text-xs font-medium shrink-0">
                  2
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <span>Tap</span>
                  <Plus className="w-5 h-5" />
                  <span>Add to Home Screen</span>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              {deferredPrompt ? (
                <Button
                  onClick={handleInstallClick}
                  className="w-full bg-white text-primary hover:bg-white/90 font-medium"
                  data-testid="install-prompt-button"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Install App
                </Button>
              ) : (
                <>
                  <p className="text-sm opacity-90">To install on Android:</p>
                  <div className="flex items-start gap-3">
                    <div className="flex items-center justify-center w-6 h-6 rounded-full bg-primary-foreground/20 text-xs font-medium shrink-0">
                      1
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <span>Tap</span>
                      <MoreVertical className="w-5 h-5" />
                      <span>Menu</span>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="flex items-center justify-center w-6 h-6 rounded-full bg-primary-foreground/20 text-xs font-medium shrink-0">
                      2
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <span>Tap "Install app" or "Add to Home Screen"</span>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* Footer hint */}
        <div className="px-4 pb-3">
          <p className="text-[10px] opacity-60 text-center">
            Get quick access and a native app experience
          </p>
        </div>
      </div>
    </div>
  );
}
